import React, { useState } from "react";
import { productsAPI } from "../services/api";
import { useI18n } from "../i18n";
import Modal from "./Modal";

const STEPS = [1, 5, 10, 20];

/**
 * Adds stock to one product without opening its whole form: type what arrived,
 * or tap a step, and see what the shelf will read. Recorded as a stock movement
 * like any other.
 */
const QuickStockDialog = ({ product, onClose, onSaved }) => {
  const { t } = useI18n();
  const [quantity, setQuantity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const amount = parseInt(quantity, 10);
  const valid = Number.isInteger(amount) && amount > 0;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;

    setSaving(true);
    setError("");

    try {
      const { data } = await productsAPI.updateStock(
        product.id,
        amount,
        t("products.quickStock.note"),
      );
      onSaved(data.product);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("products.quickStock.failed"),
      );
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("products.quickStock.title")}
      onClose={onClose}
      maxWidthClass="max-w-sm"
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <p className="font-semibold text-gray-800">{product.name}</p>
          <p className="text-sm text-gray-500">
            {t("products.quickStock.current", { count: product.stock_quantity })}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("products.quickStock.quantity")}
          </label>
          <input
            type="number"
            min="1"
            step="1"
            autoFocus
            inputMode="numeric"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full px-4 py-3 text-xl text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <div className="grid grid-cols-4 gap-2 mt-2">
            {STEPS.map((step) => (
              <button
                key={step}
                type="button"
                onClick={() => setQuantity(String((valid ? amount : 0) + step))}
                className="py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-semibold text-gray-700"
              >
                +{step}
              </button>
            ))}
          </div>
        </div>

        {valid && (
          <p className="text-center text-sm text-gray-600">
            {t("products.quickStock.newTotal", {
              count: product.stock_quantity + amount,
            })}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!valid || saving}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {t("products.quickStock.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default QuickStockDialog;
